/**
 * EmailSendersSettings — Settings > Email Senders (admin). Josh 10/12:
 * "depending on the email, I need it to be different senders".
 *
 * Sender profiles = mailboxes the app may send from. A profile never holds the
 * password: it names the Railway variable that does (e.g. SMTP_PASSWORD_COLLECTIONS),
 * so secrets stay on the server. The built-in "Default" profile is the plain SMTP_*
 * variables and is the fallback for anything unassigned. Assignments map each KIND of
 * email (bounce notices, outreach, documents, statements, …) to a profile.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, Save, Send } from 'lucide-react';
import { toast } from 'sonner';
import { tokenStorage } from '../../lib/tokenStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${tokenStorage.getToken()}` } });

const EmailSendersSettings = () => {
  const [data, setData] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/settings/email-senders`, authHeaders())
      .then((r) => {
        setData(r.data);
        setProfiles((r.data?.profiles || []).filter((p) => !p.builtin));
        setAssignments(r.data?.assignments || {});
      })
      .catch(() => toast.error('Could not load email senders'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const update = (i, patch) => setProfiles((s) => s.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const remove = (i) => setProfiles((s) => s.filter((_, idx) => idx !== i));
  const slugId = (name) => (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  // _new = never saved: the id keeps following the full name while the user types (10/23 fix — it used
  // to freeze on the first letter, so two "P…" mailboxes both became id 'p' and the save was refused).
  const add = () => setProfiles((s) => [...s, { _new: true, id: '', name: '', from_email: '', username: '', server: 'smtp.gmail.com', port: 587, password_env: '', is_legal: false }]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/settings/email-senders`, { profiles, assignments }, authHeaders());
      setData(r.data);
      setProfiles((r.data?.profiles || []).filter((p) => !p.builtin));
      setAssignments(r.data?.assignments || {});
      toast.success('Email senders saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  const sendTest = async (profileId) => {
    if (!testTo) { toast.error('Enter an address to send the test to'); return; }
    setTesting(profileId);
    try {
      const r = await axios.post(`${API}/settings/email-senders/test`, { profile_id: profileId, to: testTo }, authHeaders());
      toast.success(`Test sent from ${r.data?.from_email || profileId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test failed');
    } finally { setTesting(null); }
  };

  if (loading || !data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const builtin = (data.profiles || []).find((p) => p.builtin);
  const savedIds = new Set((data.profiles || []).map((p) => p.id));
  const configuredById = Object.fromEntries((data.profiles || []).map((p) => [p.id, p.configured]));

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Sender mailboxes</CardTitle>
          <CardDescription>
            Each profile is a Google mailbox the CRM may send from. The password is never typed here: create a Google App Password for that
            mailbox, add it on Railway (service PFS_SUITE) under the variable name you enter below, then redeploy. "Default" is the plain
            SMTP_USERNAME / SMTP_PASSWORD pair and is used for anything you leave unassigned.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {builtin && (
            <div className="rounded-lg border p-3 text-sm flex flex-wrap items-center gap-3 bg-muted/30">
              <span className="font-medium">{builtin.name}</span>
              <span className="text-muted-foreground">{builtin.from_email || 'SMTP_USERNAME not set'}</span>
              <Badge variant={builtin.configured ? 'secondary' : 'destructive'}>{builtin.configured ? 'password set' : 'SMTP_PASSWORD missing'}</Badge>
              <span className="text-xs text-muted-foreground">variables: SMTP_USERNAME · SMTP_PASSWORD · SMTP_FROM_EMAIL</span>
              <div className="flex items-center gap-2 ml-auto">
                {/* 10/14 Josh: "the area where it usually allows me to put in the test email is not there" — it sat at the
                    bottom of the list; every Test / Send test button sends to this address */}
                <Input className="h-8 w-64" placeholder="send tests to… (your email)" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="sender-test-to" />
                {builtin.configured && (
                  <Button size="sm" variant="outline" className="h-7" disabled={testing === 'env'} onClick={() => sendTest('env')}>
                    <Send className="h-3.5 w-3.5 mr-1" />{testing === 'env' ? 'Sending…' : 'Send test'}
                  </Button>
                )}
              </div>
            </div>
          )}
          {profiles.map((p, i) => (
            <div key={i} className="rounded-lg border p-3 space-y-2" data-testid={`sender-profile-${i}`}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 text-sm">
                <div className="md:col-span-3"><label className="text-xs text-muted-foreground">Name</label><Input className="h-8" value={p.name || ''} placeholder="Collections" onChange={(e) => update(i, { name: e.target.value, id: p._new || !p.id ? slugId(e.target.value) : p.id })} /></div>
                <div className="md:col-span-4"><label className="text-xs text-muted-foreground">From (what the recipient sees)</label><Input className="h-8" value={p.from_email || ''} placeholder="PFS Collections <collections@powerfundingsolutions.com>" onChange={(e) => update(i, { from_email: e.target.value })} /></div>
                <div className="md:col-span-5"><label className="text-xs text-muted-foreground">Google login (blank = the From address)</label><Input className="h-8" value={p.username || ''} placeholder="collections@powerfundingsolutions.com" onChange={(e) => update(i, { username: e.target.value })} /></div>
                <div className="md:col-span-4"><label className="text-xs text-muted-foreground">Railway variable holding the app password</label><Input className="h-8 font-mono" value={p.password_env || ''} placeholder="SMTP_PASSWORD_COLLECTIONS" onChange={(e) => update(i, { password_env: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') })} /></div>
                <div className="md:col-span-3"><label className="text-xs text-muted-foreground">SMTP server</label><Input className="h-8" value={p.server || ''} onChange={(e) => update(i, { server: e.target.value })} /></div>
                <div className="md:col-span-2"><label className="text-xs text-muted-foreground">Port</label><Input className="h-8" type="number" value={p.port ?? 587} onChange={(e) => update(i, { port: Number(e.target.value) })} /></div>
                <div className="md:col-span-4 flex items-end">
                  {/* 10/13 Josh: legal mailboxes are offered only for MERCHANT emails; broker / document mail sees admin mailboxes only */}
                  <label className="flex items-center gap-2 text-xs text-muted-foreground h-8 cursor-pointer" title="Offered in the From dropdown only for emails to the merchant (bounce notices, merchant outreach, compose to merchant). Broker outreach, deal documents and the default package never list it.">
                    <input type="checkbox" checked={!!p.is_legal} onChange={(e) => update(i, { is_legal: e.target.checked })} data-testid={`sender-profile-legal-${i}`} />
                    Legal department mailbox (merchant emails only)
                  </label>
                </div>
                <div className="md:col-span-8 flex items-end gap-2">
                  {savedIds.has(p.id) ? (
                    <Badge variant={configuredById[p.id] ? 'secondary' : 'destructive'}>{configuredById[p.id] ? 'password set' : `${p.password_env} not set on Railway`}</Badge>
                  ) : <Badge variant="outline">unsaved</Badge>}
                  {savedIds.has(p.id) && configuredById[p.id] && (
                    <Button size="sm" variant="outline" className="h-7" disabled={testing === p.id} onClick={() => sendTest(p.id)}><Send className="h-3.5 w-3.5 mr-1" />{testing === p.id ? 'Sending…' : 'Test'}</Button>
                  )}
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600 ml-auto" onClick={() => remove(i)} title="Remove"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={add}><Plus className="h-4 w-4 mr-1" />Add mailbox</Button>
            <span className="text-xs text-muted-foreground ml-auto">Test / Send test buttons send to the address in the Default row above.</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Who sends what</CardTitle>
          <CardDescription>
            Default sender per email kind. For mail sent on behalf of a deal, the company columns override it by the deal's
            funding company (same rule as the letters) and by its stage: <strong>Active</strong> = any live deal,
            <strong> Legal</strong> = Default / written-off deals or any deal with a recovery status. "— inherit —" uses the
            next level up. Profiles whose password variable is missing fall back to Default.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {(() => {
            const entities = data.entities || [];
            const stages = data.stages || [];
            const cols = `minmax(220px,1.3fr) minmax(200px,1fr) repeat(${entities.length}, minmax(220px,1fr))`;
            const profileItems = profiles.filter((p) => p.id).map((p) => <SelectItem key={p.id} value={p.id}>{p.name || p.id}</SelectItem>);
            const Eff = ({ e }) => e ? (
              <div className="text-[11px] text-muted-foreground truncate">
                as <span className="font-medium">{e.from_email || '—'}</span>
                {e.fallback_from ? <span className="text-amber-600"> (fell back: {e.fallback_from} has no password)</span> : null}
                {!e.configured ? <span className="text-red-600"> · not configured</span> : null}
              </div>
            ) : null;
            const Pick = ({ k, inheritLabel }) => (
              <Select value={assignments[k] || 'env'} onValueChange={(v) => setAssignments((a) => ({ ...a, [k]: v === 'env' ? null : v }))}>
                <SelectTrigger className="h-8" data-testid={`sender-assign-${k}`}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="env">{inheritLabel}</SelectItem>{profileItems}</SelectContent>
              </Select>
            );
            return (
              <>
                <div className="hidden md:grid text-xs font-medium text-muted-foreground border-b pb-1 gap-2" style={{ gridTemplateColumns: cols }}>
                  <div>Email kind</div>
                  <div>Default sender</div>
                  {entities.map((en) => <div key={en.key}>{en.name} deals</div>)}
                </div>
                {(data.kinds || []).map((k) => {
                  const eff = data.effective?.[k.id];
                  return (
                    <div key={k.id} className="grid gap-2 border-b py-2 text-sm items-start" style={{ gridTemplateColumns: cols }}>
                      <div>{k.label}{!k.deal_bound && <div className="text-[11px] text-muted-foreground">not tied to one deal — Default only</div>}</div>
                      <div><Pick k={k.id} inheritLabel="Default" /><Eff e={eff} /></div>
                      {entities.map((en) => (
                        <div key={en.key} className="space-y-1">
                          {!k.deal_bound
                            ? <span className="text-[11px] text-muted-foreground">—</span>
                            : k.staged
                              ? stages.map((st) => (
                                <div key={st.id} className="flex items-start gap-2">
                                  <span className="text-[11px] text-muted-foreground w-12 pt-2 shrink-0">{st.id === 'legal' ? 'Legal' : 'Active'}</span>
                                  <div className="flex-1 min-w-0">
                                    <Pick k={`${k.id}@${en.key}#${st.id}`} inheritLabel="— inherit —" />
                                    <Eff e={eff?.by_entity?.[en.key]?.[st.id]} />
                                  </div>
                                </div>
                              ))
                              : (<div>
                                  <Pick k={`${k.id}@${en.key}`} inheritLabel="— inherit —" />
                                  <Eff e={eff?.by_entity?.[en.key]?.any} />
                                </div>)}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </>
            );
          })()}
          <div className="pt-2">
            <Button size="sm" onClick={save} disabled={saving} data-testid="email-senders-save"><Save className="h-4 w-4 mr-1" />{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailSendersSettings;
